import React from 'react';
import { clsx } from 'clsx';
import { Pie, PieChart, ResponsiveContainer, Tooltip as RechartTooltip } from 'recharts';

const COLOR_CUSTOMER_REQUEST = '#54DAE2';
const COLOR_WORKFLOW_RULE = '#F9AE00';
const COLOR_KNOWLEDGE_GAP = '#E52020';
const COLOR_OTHER_ESCALATIONS = '#DAE0E2';
const COLOR_EMPTY = '#F1F6F6';

type EscalationTrendsCardProps = {
  customerRequest?: number;
  workflowRule?: number;
  knowledgeGap?: number;
  otherEscalations?: number;
  title?: string;
  className?: string;
};

type TooltipPayloadEntry = { name?: string; value?: number };
type CustomTooltipProps = { active?: boolean; payload?: TooltipPayloadEntry[] };

const CustomTooltip = ({ active = false, payload = [] }: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white rounded-sm-5 shadow-tooltip text-xs py-1.5 px-3 flex flex-row gap-2 items-center font-jamjuree">
        <div className="flex items-center gap-0.5">
          <p className="font-medium">{`${payload[0].name} -`}</p>
          <p className="font-bold text-sm">{`${payload[0].value}`}</p>
        </div>
      </div>
    );
  }
  return null;
};

export const EscalationTrendsCard: React.FC<EscalationTrendsCardProps> = ({
  customerRequest = 0,
  workflowRule = 0,
  knowledgeGap = 0,
  otherEscalations = 0,
  title = 'Escalation trends by type',
  className,
}) => {
  const segments = [
    { name: 'Customer request', value: customerRequest, fill: COLOR_CUSTOMER_REQUEST },
    { name: 'Workflow rule', value: workflowRule, fill: COLOR_WORKFLOW_RULE },
    { name: 'Knowledge gap', value: knowledgeGap, fill: COLOR_KNOWLEDGE_GAP },
    { name: 'Other escalations', value: otherEscalations, fill: COLOR_OTHER_ESCALATIONS },
  ];

  const total = segments.reduce((sum, s) => sum + s.value, 0);
  const data = total > 0 ? segments : [{ name: 'Empty', value: 1, fill: COLOR_EMPTY }];

  return (
    <div
      className={clsx(
        'flex flex-col items-center gap-4 p-4 w-full h-full bg-white border border-[#F1F6F6] rounded-sm-5 font-jamjuree',
        className,
      )}
    >
      <div className="flex items-center w-full h-8">
        <span className="text-sm font-medium leading-[1.5] text-text_secondary">{title}</span>
      </div>

      <div className="flex items-center justify-center w-full flex-1 min-h-[260px]">
        <div className="relative size-[260px] shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={90}
                outerRadius={130}
                startAngle={90}
                endAngle={-270}
                dataKey="value"
                stroke="none"
                isAnimationActive={total > 0}
              />
              {total > 0 && <RechartTooltip content={<CustomTooltip />} />}
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
